import { createContext, useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { MeResponse, ModuleCode, PermissionCode, RoleCode } from '@pms/shared';
import { ROLES } from '@pms/shared';
import { tokenStorage } from '@/lib/token-storage';
import { setAuthFailureHandler } from '@/lib/api';
import { queryClient } from '@/lib/queryClient';
import { authService } from '@/services/auth.service';
import { profileService } from '@/services/profile.service';
import { rolesService } from '@/services/roles.service';

export interface AuthContextValue {
  user: MeResponse['user'] | null;
  profile: MeResponse['profile'];
  photo_url: string | null;
  // Effective values (apply dev impersonation if active)
  roles: RoleCode[];
  permissions: PermissionCode[];
  modules: ModuleCode[];
  isSuperAdmin: boolean;
  // Real values (always from /me, never impersonated)
  actualRoles: RoleCode[];
  actualIsSuperAdmin: boolean;
  // Dev-only: view as a specific role
  viewAsRole: RoleCode | null;
  setViewAsRole: (code: RoleCode | null) => void;

  isAuthenticated: boolean;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshMe: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

const VIEW_AS_KEY = 'pms.dev.viewAsRole';

function loadViewAsRole(): RoleCode | null {
  if (!import.meta.env.DEV) return null;
  try {
    const v = sessionStorage.getItem(VIEW_AS_KEY);
    return v ? (v as RoleCode) : null;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [me, setMe] = useState<MeResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [viewAsRole, setViewAsRoleState] = useState<RoleCode | null>(loadViewAsRole);

  const loadMe = useCallback(async () => {
    if (!tokenStorage.getAccessToken()) {
      setMe(null);
      return;
    }
    try {
      const data = await profileService.getMe();
      setMe(data);
    } catch {
      tokenStorage.clear();
      setMe(null);
    }
  }, []);

  useEffect(() => {
    let mounted = true;
    (async () => {
      await loadMe();
      if (mounted) setLoading(false);
    })();
    return () => {
      mounted = false;
    };
  }, [loadMe]);

  useEffect(() => {
    setAuthFailureHandler(() => {
      setMe(null);
      // Forced logout on an expired/invalid token. Leaks the same way logout() would
      // without this, and fires with no user action, so it must clear too.
      queryClient.clear();
    });
  }, []);

  const login = useCallback(
    async (email: string, password: string) => {
      await authService.login(email, password);
      await loadMe();
    },
    [loadMe],
  );

  const logout = useCallback(async () => {
    await authService.logout();
    setMe(null);
    setViewAsRoleState(null);
    // gcTime is 0, but a query still mounted at this moment keeps its data. Without this,
    // logging in as someone else in the same tab can flash the previous user's rows.
    queryClient.clear();
    try {
      sessionStorage.removeItem(VIEW_AS_KEY);
    } catch {
      /* noop */
    }
  }, []);

  const setViewAsRole = useCallback((code: RoleCode | null) => {
    setViewAsRoleState(code);
    // Permissions and modules change, so anything already fetched is answering the
    // previous role's question.
    queryClient.clear();
    try {
      if (code) sessionStorage.setItem(VIEW_AS_KEY, code);
      else sessionStorage.removeItem(VIEW_AS_KEY);
    } catch {
      /* noop */
    }
  }, []);

  // When impersonating, fetch the role's permissions + default modules
  const { data: impersonatedRole } = useQuery({
    queryKey: ['role-impersonation', viewAsRole],
    queryFn: () => rolesService.get(viewAsRole as string),
    enabled: !!viewAsRole && viewAsRole !== ROLES.SUPER_ADMIN,
  });

  const value = useMemo<AuthContextValue>(() => {
    const actualRoles = me?.roles ?? [];
    const actualIsSuperAdmin = me?.isSuperAdmin ?? false;

    let effectiveRoles = actualRoles;
    let effectivePerms = me?.permissions ?? [];
    let effectiveModules = me?.modules ?? [];
    let effectiveIsSuperAdmin = actualIsSuperAdmin;

    if (viewAsRole && me) {
      effectiveRoles = [viewAsRole];
      if (viewAsRole === ROLES.SUPER_ADMIN) {
        effectiveIsSuperAdmin = true;
        effectivePerms = [];
        effectiveModules = me.modules; // doesn't matter, bypassed
      } else {
        effectiveIsSuperAdmin = false;
        effectivePerms = impersonatedRole?.permissions ?? [];
        effectiveModules = impersonatedRole?.default_modules ?? [];
      }
    }

    return {
      user: me?.user ?? null,
      profile: me?.profile ?? null,
      photo_url: me?.photo_url ?? null,
      roles: effectiveRoles,
      permissions: effectivePerms,
      modules: effectiveModules,
      isSuperAdmin: effectiveIsSuperAdmin,
      actualRoles,
      actualIsSuperAdmin,
      viewAsRole,
      setViewAsRole,
      isAuthenticated: !!me,
      loading,
      login,
      logout,
      refreshMe: loadMe,
    };
  }, [me, viewAsRole, impersonatedRole, loading, login, logout, loadMe, setViewAsRole]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
